import React, { FC, memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { IconButton, Link, Tooltip, Dialog, DialogTitle, DialogContent, TitleText, useToggle } from "@evergis/ui";

import {
  FooterButtons,
  FooterLogoContainer,
  LinkButton,
  SocialButtons,
  SocialDivider,
  DialogActionsGroup,
} from "./styled";
import { LoginScreenLink, LoginScreenNetwork } from "./types";
import { PortalConfiguration, getPortalConfigSelector } from "../../ducks/portalConfig";

const SignInFooterComponent: FC<PortalConfiguration["settings"]["loginScreen"]["footer"]> = ({
  logo,
  logoLink,
  networks,
  links,
}) => {
  const { t } = useTranslation();
  const [isAboutDialogOpen, toggleAboutDialogOpen] = useToggle();
  const config = useSelector(getPortalConfigSelector);

  const aboutDialogContent = useMemo(
    () =>
      config?.settings?.aboutDialog
        ? DOMPurify.sanitize(marked.parse(config.settings.aboutDialog) as string)
        : t("noDescription"),
    [config?.settings?.aboutDialog],
  );

  const renderLogoLink = useMemo(() => (logoLink?.text ? t(logoLink.text) : ""), [logoLink?.text]);

  const isAboutDialogLink = logoLink?.dialog === "about";

  return (
    <>
      {!!logo && (
        <FooterLogoContainer hasLogoLink={!!logoLink}>
          <a href="https://evergis.ai/" target="_blank" rel="noopener noreferrer">
            <img src={logo} alt="" className="footer-image" />
          </a>
          {!!logoLink && (
            <Link
              href={isAboutDialogLink ? "#" : logoLink.url}
              target={isAboutDialogLink ? undefined : "_blank"}
              onClick={(e) => {
                if (isAboutDialogLink) {
                  e.preventDefault();
                  toggleAboutDialogOpen();
                }
              }}
            >
              {logoLink.icon ? <LinkButton kind={logoLink.icon ?? ""}>{renderLogoLink}</LinkButton> : renderLogoLink}
            </Link>
          )}
        </FooterLogoContainer>
      )}
      {(!!networks?.length || !!links?.length) && (
        <FooterButtons>
          {!!networks?.length && (
            <SocialButtons>
              {networks?.map(({ icon, url, tooltip }: LoginScreenNetwork, index: number) => (
                <Tooltip key={index} content={tooltip ? t(tooltip) : url || ""} placement={"top"} distance={0} arrow>
                  {(ref?: (node: HTMLElement | null) => void) => (
                    <Link href={url} target={"_blank"}>
                      <IconButton innerRef={ref} kind={icon} />
                    </Link>
                  )}
                </Tooltip>
              ))}
            </SocialButtons>
          )}
          {!!networks?.length && !!links?.length && <SocialDivider />}
          {links?.map(({ icon, text, url, tooltip }: LoginScreenLink, index: number) => (
            <Tooltip key={index} content={tooltip ? t(tooltip) : url || ""} placement={"top"} distance={0} arrow>
              {(ref?: (node: HTMLElement | null) => void) => (
                <Link href={url} target={"_blank"}>
                  <LinkButton innerRef={ref} kind={icon}>
                    {text ? t(text) : ""}
                  </LinkButton>
                </Link>
              )}
            </Tooltip>
          ))}
        </FooterButtons>
      )}
      <Dialog maxWidth="53rem" isOpen={isAboutDialogOpen} onCloseRequest={toggleAboutDialogOpen}>
        <DialogTitle>
          <DialogActionsGroup>
            <TitleText>{t("about")}</TitleText>
            <IconButton kind="close" onClick={toggleAboutDialogOpen} />
          </DialogActionsGroup>
        </DialogTitle>
        <DialogContent style={{ marginLeft: "1rem" }}>
          <div dangerouslySetInnerHTML={{ __html: aboutDialogContent }} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const SignInFooter: FC = memo(SignInFooterComponent);
